import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import CartSidebar from "@/components/feature/CartSidebar";
import ProductCard from "@/components/base/ProductCard";
import { useProducts } from "@/hooks/useProducts";

const CATEGORY_TABS = [
  { label: "All New", value: "all", icon: "ri-sparkling-2-line" },
  { label: "i-clothes", value: "clothes", icon: "ri-t-shirt-line" },
  { label: "i-shoes", value: "shoes", icon: "ri-footprint-line" },
  { label: "i-bag", value: "bags", icon: "ri-handbag-line" },
  { label: "i-nails", value: "nails", icon: "ri-hand-coin-line" },
];

const SORT_OPTIONS = [
  { label: "Latest First", value: "newest" },
  { label: "Price: Low to High", value: "price_asc" },
  { label: "Price: High to Low", value: "price_desc" },
  { label: "Best Rated", value: "rating" },
  { label: "Featured", value: "featured" },
];

const CATEGORY_HIGHLIGHTS = [
  {
    category: "clothes",
    label: "i-clothes",
    tagline: "New Silhouettes",
    image:
      "https://readdy.ai/api/search-image?query=three%20fashion%20models%20wearing%20new%20collection%20of%20lustrous%20satin%20and%20velvet%20dresses%20in%20silver%20ivory%20and%20midnight%20blue%2C%20studio%20fashion%20campaign%20photography%2C%20editorial%20style%2C%20moonlight%20aesthetic%2C%20elegant%20poses%2C%20dark%20atmospheric%20background%20with%20soft%20dramatic%20lighting&width=600&height=750&seq=iif_na_cat_clothes&orientation=portrait",
    href: "/collections/clothes",
    count: 3,
  },
  {
    category: "shoes",
    label: "i-shoes",
    tagline: "New Steps",
    image:
      "https://readdy.ai/api/search-image?query=luxury%20editorial%20composition%20of%20new%20metallic%20strappy%20sandals%20and%20patent%20ankle%20boots%20displayed%20on%20dark%20polished%20marble%2C%20dramatic%20studio%20lighting%20with%20reflections%2C%20silver%20gold%20tones%2C%20high%20fashion%20shoe%20photography%2C%20atmospheric%20moonlight%20glow&width=600&height=750&seq=iif_na_cat_shoes&orientation=portrait",
    href: "/collections/shoes",
    count: 2,
  },
  {
    category: "bags",
    label: "i-bag",
    tagline: "New Arrivals",
    image:
      "https://readdy.ai/api/search-image?query=luxury%20new%20bag%20collection%20editorial%20photography%20featuring%20iridescent%20holographic%20shoulder%20bag%20gold%20metallic%20mini%20box%20bag%20and%20velvet%20bucket%20bag%20arranged%20on%20dark%20velvet%20surface%20with%20individual%20dramatic%20spotlights%2C%20moonlight%20silver%20gold%20tones%2C%20high%20fashion%20campaign&width=600&height=750&seq=iif_na_cat_bags&orientation=portrait",
    href: "/collections/bags",
    count: 3,
  },
  {
    category: "nails",
    label: "i-nails",
    tagline: "New Glow",
    image:
      "https://readdy.ai/api/search-image?query=elegant%20female%20hands%20showcasing%20new%20cat%20eye%20press%20on%20nails%20with%20moonlight%20white%20and%20galaxy%20navy%20blue%20cat%20eye%20magnetic%20shimmer%20effect%2C%20close%20up%20beauty%20editorial%20photography%2C%20dark%20velvet%20background%20with%20bokeh%2C%20luxurious%20nail%20art%20campaign&width=600&height=750&seq=iif_na_cat_nails&orientation=portrait",
    href: "/collections/nails",
    count: 2,
  },
];

export default function NewArrivalsPage() {
  const [activeCategory, setActiveCategory] = useState("all");
  const [sortBy, setSortBy] = useState("newest");
  const [hoveredCategory, setHoveredCategory] = useState<string | null>(null);

  const { products: newProducts, loading } = useProducts({ isNew: true });

  const filtered = useMemo(() => {
    let list = activeCategory === "all"
      ? [...newProducts]
      : newProducts.filter((p) => p.category === activeCategory);

    switch (sortBy) {
      case "price_asc":
        list.sort((a, b) => a.price - b.price);
        break;
      case "price_desc":
        list.sort((a, b) => b.price - a.price);
        break;
      case "rating":
        list.sort((a, b) => b.rating - a.rating);
        break;
      case "featured":
        list.sort((a, b) => (b.isFeatured ? 1 : 0) - (a.isFeatured ? 1 : 0));
        break;
      default:
        break;
    }
    return list;
  }, [newProducts, activeCategory, sortBy]);

  const categoryCounts = useMemo(() => {
    const counts: Record<string, number> = { all: newProducts.length };
    newProducts.forEach((p) => {
      counts[p.category] = (counts[p.category] || 0) + 1;
    });
    return counts;
  }, [newProducts]);

  return (
    <div
      className="min-h-screen bg-moonlight"
      style={{ fontFamily: "'Jost', sans-serif" }}
    >
      <Navbar />
      <CartSidebar />

      {/* Hero */}
      <section className="relative pt-20 lg:pt-24 overflow-hidden">
        <div className="relative h-[380px] md:h-[480px]">
          <img
            src="https://readdy.ai/api/search-image?query=luxurious%20new%20arrivals%20fashion%20editorial%20flat%20lay%20composition%20featuring%20silver%20satin%20dress%20metallic%20strappy%20heels%20iridescent%20chain%20bag%20cat%20eye%20press%20on%20nails%20and%20gold%20metallic%20mini%20bag%20arranged%20on%20deep%20midnight%20blue%20velvet%20surface%2C%20individual%20spotlights%20illuminating%20each%20piece%2C%20dramatic%20editorial%20fashion%20photography%20campaign%2C%20silver%20gold%20moonlight%20tones%2C%20cross%20category%20luxury%20fashion&width=1440&height=600&seq=iif_na_hero_001&orientation=landscape"
            alt="New Arrivals at i-if"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-midnight/60 via-midnight/40 to-midnight/70" />

          <div className="relative h-full flex flex-col items-center justify-center text-center px-6">
            <p className="text-[10px] tracking-[0.4em] uppercase text-moonlight/50 mb-3">
              — Spring 2026 —
            </p>
            <h1
              className="text-5xl md:text-6xl lg:text-7xl font-light italic text-moonlight mb-4"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              New Arrivals
            </h1>
            <p className="text-sm text-moonlight/60 tracking-widest uppercase mb-1">
              Fresh Glow Across All Categories
            </p>
            <div className="w-12 h-px bg-moonlight/30 mt-5 mb-5" />
            <div className="flex items-center gap-6">
              {CATEGORY_HIGHLIGHTS.map((cat) => (
                <Link
                  key={cat.category}
                  to={cat.href}
                  className="flex flex-col items-center gap-1 group cursor-pointer"
                >
                  <span className="text-[10px] tracking-widest uppercase text-moonlight/40 group-hover:text-moonlight/80 transition-colors">
                    {cat.label}
                  </span>
                  <span className="text-lg font-light text-moonlight/60 group-hover:text-moonlight transition-colors">
                    {cat.count}
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Category Highlight Cards */}
      <section className="bg-moonlight-warm border-b border-pearl/40">
        <div className="max-w-7xl mx-auto px-5 lg:px-16 py-10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {CATEGORY_HIGHLIGHTS.map((cat) => (
              <Link
                key={cat.category}
                to={cat.href}
                className="relative overflow-hidden group cursor-pointer"
                onMouseEnter={() => setHoveredCategory(cat.category)}
                onMouseLeave={() => setHoveredCategory(null)}
              >
                <div className="relative h-52 md:h-64 overflow-hidden">
                  <img
                    src={cat.image}
                    alt={cat.label}
                    className={`w-full h-full object-cover object-top transition-transform duration-700 ${
                      hoveredCategory === cat.category ? "scale-105" : "scale-100"
                    }`}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-midnight/80 via-midnight/20 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <p className="text-[9px] tracking-widest uppercase text-moonlight/50 mb-0.5">
                      {cat.tagline}
                    </p>
                    <div className="flex items-center justify-between">
                      <p
                        className="text-lg font-light italic text-moonlight"
                        style={{ fontFamily: "'Cormorant Garamond', serif" }}
                      >
                        {cat.label}
                      </p>
                      <span className="text-[10px] text-moonlight/50 border border-moonlight/20 px-2 py-0.5">
                        {cat.count} New
                      </span>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Product Section */}
      <div className="max-w-7xl mx-auto px-5 lg:px-16 py-10">
        {/* Category Tabs + Sort */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8 pb-6 border-b border-pearl/40">
          {/* Category Tabs */}
          <div className="flex items-center gap-1 bg-moonlight-warm p-1 rounded-full">
            {CATEGORY_TABS.map((tab) => (
              <button
                key={tab.value}
                onClick={() => setActiveCategory(tab.value)}
                className={`flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs transition-all cursor-pointer whitespace-nowrap ${
                  activeCategory === tab.value
                    ? "bg-midnight text-moonlight"
                    : "text-midnight/50 hover:text-midnight"
                }`}
              >
                <i className={`${tab.icon} text-sm`} />
                {tab.label}
                {categoryCounts[tab.value] !== undefined && (
                  <span
                    className={`text-[10px] ${
                      activeCategory === tab.value
                        ? "text-moonlight/60"
                        : "text-midnight/30"
                    }`}
                  >
                    ({categoryCounts[tab.value]})
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* Sort */}
          <div className="flex items-center gap-3 shrink-0">
            <span className="text-[10px] text-midnight/40 tracking-wider uppercase hidden sm:block">
              {filtered.length} piece{filtered.length !== 1 ? "s" : ""}
            </span>
            <div className="relative">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="text-xs tracking-wide border border-pearl bg-transparent text-midnight px-3 py-2.5 pr-7 appearance-none cursor-pointer focus:outline-none focus:border-midnight"
              >
                {SORT_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <i className="ri-arrow-down-s-line absolute right-2 top-1/2 -translate-y-1/2 text-midnight/40 pointer-events-none text-sm" />
            </div>
          </div>
        </div>

        {/* Drop Date Banner */}
        <div className="mb-8 flex items-center gap-4 px-5 py-4 border-l-2 border-midnight/30 bg-moonlight-warm">
          <div className="w-8 h-8 flex items-center justify-center shrink-0">
            <i className="ri-time-line text-midnight/40 text-lg" />
          </div>
          <div>
            <p className="text-xs font-medium text-midnight tracking-wide">
              Spring 2026 Drop — Now Available
            </p>
            <p className="text-[11px] text-midnight/40 mt-0.5">
              {newProducts.length} new styles added across Clothes, Shoes, Bags & Nails. Free shipping on orders over $80.
            </p>
          </div>
          <div className="ml-auto flex gap-2 shrink-0">
            {["clothes", "shoes", "bags", "nails"].map((cat) => (
              <span
                key={cat}
                className="text-[9px] tracking-widest uppercase border border-midnight/20 text-midnight/40 px-2 py-1 hidden md:block"
              >
                {cat}
              </span>
            ))}
          </div>
        </div>

        {/* Product Grid */}
        {loading ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5 lg:gap-7">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="animate-pulse">
                <div className="bg-pearl/30 aspect-[3/4] mb-3" />
                <div className="bg-pearl/30 h-4 w-3/4 mb-2" />
                <div className="bg-pearl/30 h-3 w-1/2" />
              </div>
            ))}
          </div>
        ) : filtered.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5 lg:gap-7">
            {filtered.map((product) => (
              <div key={product.id} className="relative">
                {/* Category Badge */}
                <div className="absolute top-3 left-3 z-10">
                  <span className="text-[9px] tracking-widest uppercase bg-midnight text-moonlight px-2 py-1">
                    {product.category === "clothes"
                      ? "i-clothes"
                      : product.category === "shoes"
                      ? "i-shoes"
                      : product.category === "bags"
                      ? "i-bag"
                      : "i-nails"}
                  </span>
                </div>
                <ProductCard product={product} />
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-24">
            <i className="ri-inbox-line text-3xl text-midnight/20 mb-4 block" />
            <p className="text-sm text-midnight/40">No new arrivals in this category yet.</p>
          </div>
        )}

        {/* The Full Collection CTA */}
        <div className="mt-20 grid grid-cols-1 md:grid-cols-2 gap-px bg-pearl/30">
          {/* Left — dark panel */}
          <div
            className="relative overflow-hidden"
            style={{ background: "linear-gradient(135deg, #0F1C24 60%, #1A2A38 100%)" }}
          >
            <img
              src="https://readdy.ai/api/search-image?query=moody%20atmospheric%20luxury%20fashion%20flat%20lay%20on%20midnight%20black%20surface%2C%20silver%20moonlight%20satin%20fabric%20draped%20elegantly%20alongside%20pearl%20clutch%20bag%20and%20crystal%20kitten%20heels%2C%20soft%20studio%20moonlight%20glow%2C%20dark%20and%20luxurious%20editorial%20photography%2C%20i-if%20brand%20aesthetic&width=700&height=500&seq=iif_na_cta_left&orientation=landscape"
              alt="i-if full collection"
              className="absolute inset-0 w-full h-full object-cover object-center opacity-30"
            />
            <div className="relative px-10 py-14">
              <p className="text-[10px] tracking-[0.4em] uppercase text-moonlight/40 mb-4">
                The Full Collection
              </p>
              <h2
                className="text-3xl font-light italic text-moonlight mb-4 leading-snug"
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
              >
                Everything that glows,<br />in one place.
              </h2>
              <p className="text-xs text-moonlight/50 leading-relaxed max-w-xs">
                Clothes, shoes, bags, and nails — all united by a single obsession with luminosity. Build your complete glow from head to toe.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                {[
                  { label: "i-clothes", href: "/collections/clothes" },
                  { label: "i-shoes", href: "/collections/shoes" },
                  { label: "i-bag", href: "/collections/bags" },
                  { label: "i-nails", href: "/collections/nails" },
                ].map((link) => (
                  <Link
                    key={link.href}
                    to={link.href}
                    className="text-[10px] tracking-widest uppercase border border-moonlight/25 text-moonlight/70 px-4 py-2 hover:border-moonlight/60 hover:text-moonlight transition-all cursor-pointer whitespace-nowrap"
                  >
                    {link.label}
                  </Link>
                ))}
              </div>
            </div>
          </div>

          {/* Right — light panel */}
          <div className="bg-moonlight-warm px-10 py-14 flex flex-col justify-center">
            <p className="text-[10px] tracking-[0.4em] uppercase text-midnight/40 mb-4">
              The Glow Promise
            </p>
            <h2
              className="text-3xl font-light italic text-midnight mb-6 leading-snug"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              What makes something<br />i-if worthy?
            </h2>
            <div className="space-y-5">
              {[
                {
                  icon: "ri-moon-line",
                  title: "Luminosity First",
                  desc: "Every piece we carry catches the light. No exceptions. If it doesn't glow, it doesn't belong here.",
                },
                {
                  icon: "ri-leaf-line",
                  title: "Quality Over Quantity",
                  desc: "Curated collections, not overwhelming catalogues. We only bring you the pieces that matter.",
                },
                {
                  icon: "ri-sparkling-line",
                  title: "Head-to-Toe Glow",
                  desc: "From your dress to your shoes, bag, and nails — we cover every touchpoint of shine.",
                },
              ].map((item) => (
                <div key={item.title} className="flex gap-4">
                  <div className="w-8 h-8 flex items-center justify-center shrink-0">
                    <i className={`${item.icon} text-midnight/40 text-lg`} />
                  </div>
                  <div>
                    <p className="text-xs font-medium text-midnight mb-1">{item.title}</p>
                    <p className="text-[11px] text-midnight/50 leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Shipping Strip */}
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-px bg-pearl/30">
          {[
            {
              icon: "ri-truck-line",
              title: "Free US Shipping",
              desc: "On orders over $80",
            },
            {
              icon: "ri-refresh-line",
              title: "Easy Returns",
              desc: "30-day hassle-free policy",
            },
            {
              icon: "ri-customer-service-line",
              title: "Glow Concierge",
              desc: "Styling help, always",
            },
          ].map((item) => (
            <div
              key={item.title}
              className="bg-moonlight-warm px-6 py-5 flex items-center gap-4"
            >
              <div className="w-9 h-9 flex items-center justify-center shrink-0">
                <i className={`${item.icon} text-lg text-midnight/40`} />
              </div>
              <div>
                <p className="text-xs font-medium text-midnight">{item.title}</p>
                <p className="text-[10px] text-midnight/40 mt-0.5">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <Footer />
    </div>
  );
}
